import React from 'react';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { Hidden } from '@material-ui/core';
import { FormikProps } from 'formik';
import { Beneficiary, BeneficiaryUpdatePageModel, beneficiaryPrimaryStatus } from './model/beneficiary-models';
import { BeneficiarySettings } from './model/epi-props';
import { formatDate } from '../../../../global/helpers/form-data-helper';
import { FormField } from '../../../../global/components/form-element/form-field';
import { LabelSettingProps } from '../../../../global/models/epi-model';
import { NumberField, NumberInputTypes } from '../../../../global/components/form-element/number-field';
import { GetMappedRelationshipByValue, GetMappedStatusByValue } from './helper/beneficiary-helper';
import { buttonStyles } from '../../../../global/constants/theme/styles/components/buttonstyles';

interface Props {
	beneficiary: Beneficiary;
	index: number;
	fieldName: string,
	beneficiarySettings: BeneficiarySettings;
	labelSetting: LabelSettingProps;
	formikProps: FormikProps<BeneficiaryUpdatePageModel>;
	beneficiaryStatusOptions: any[];
	isUniquePrimary?: boolean;
	isStatusEditable: boolean;
	isRemovable: boolean;
	mappedRelationship: any[];
	hasPercentageMissingValues: boolean;
	hasIncorrectTotal: boolean;
	hasPercentages: boolean;
	statusEditableOnlyForNotDesignated?: boolean,
	onChangeStatus?: (formikProps: FormikProps<BeneficiaryUpdatePageModel>, beneficiary: Beneficiary, newStatus) => void;
	onRemove?: (formikProps: FormikProps<BeneficiaryUpdatePageModel>, beneficiary: Beneficiary) => void;
}
export function BeneficiaryRowView(props: Props) {

	const buttonClasses = buttonStyles();
	const { beneficiary, index, fieldName, beneficiarySettings, isUniquePrimary,
		labelSetting, beneficiaryStatusOptions, formikProps, isStatusEditable, mappedRelationship,
		hasPercentageMissingValues, hasIncorrectTotal, isRemovable, hasPercentages, statusEditableOnlyForNotDesignated,
		onChangeStatus, onRemove } = props;

	const showPercentageRedWarning = beneficiary?.participation && hasIncorrectTotal;
	const showPercentageGreenCheck = beneficiary?.participation > 0 && beneficiary?.participation < 100 && !hasPercentageMissingValues && !hasIncorrectTotal;

	if (!beneficiary) return null;

	function StatusChanged(e) {
		const { value } = e.target;

		if (onChangeStatus) {
			onChangeStatus(formikProps, beneficiary, value);
		}
	}

	function Remove() {
		if (onRemove) {
			onRemove(formikProps, beneficiary);
		}
	}

	function RenderStatus() {
		if (beneficiaryStatusOptions?.length > 0 && isStatusEditable &&
			(!statusEditableOnlyForNotDesignated || (statusEditableOnlyForNotDesignated && beneficiary.status !== beneficiaryPrimaryStatus))) {
			return (
				<FormField
					name={`${fieldName}.${index.toString()}.status`}
					component='input'
					options={beneficiaryStatusOptions}
					errors={formikProps.errors}
					touched={formikProps.touched}
					select
					placeHolder={labelSetting.selectDefaultText}
					onChange={(e) => StatusChanged(e)}
				/>);
		}

		return (<>{GetMappedStatusByValue(beneficiary.status, beneficiarySettings)}</>);
	}

	function RenderGreenCheck() {
		if (!showPercentageGreenCheck) return null;

		return (
			<CheckCircleIcon className='green-check' />
		);
	}

	function RenderPercentage() {
		if (!hasPercentages) return null;

		if (!isUniquePrimary) {
			return (
				<div className={`benefit-table-row-cell-number-field ${showPercentageRedWarning ? 'benefit-table-row-cell-number-field-invalid' : ''} ${showPercentageGreenCheck ? 'benefit-table-row-cell-number-field-valid' : ''}`}>
					<NumberField
						name={`${fieldName}.${index.toString()}.participation`}
						label=''
						type={NumberInputTypes.PercentageInteger}
						formikProps={formikProps}
					/>
					<Hidden mdDown>
						{RenderGreenCheck()}
					</Hidden>
				</div>);
		}

		return (<>{beneficiary.participation}%</>);
	}

	function RenderAction() {

		if (isUniquePrimary && !isRemovable) return null;

		return (<button className={buttonClasses.linkButton} type='button' onClick={Remove} key={beneficiary?.id}><strong>{beneficiarySettings?.removeLinkText}</strong></button>);
	}

	return (
		<React.Fragment key={beneficiary?.id}>
			<div className='benefit-table-row'>
				<div className='benefit-table-row-cell benefit-table-row-cell-name'>
					<Hidden smUp>
						<p className='input-label'>{beneficiarySettings?.nameColumnLabel}</p>
					</Hidden>
					<div>
						{beneficiary.firstName} {beneficiary.lastName}
					</div>
					<Hidden mdUp>
						{RenderAction()}
					</Hidden>
				</div>
				<div className='benefit-table-row-cell'>
					<Hidden smUp>
						<p className='input-label'>{beneficiarySettings?.dateOfBirthColumnLabel}</p>
					</Hidden>
					<div>
						{beneficiary.dob ? formatDate(beneficiary.dob, true, true) : ''}
					</div>
				</div>
				<div className='benefit-table-row-cell'>
					<Hidden smUp>
						<p className='input-label'>{beneficiarySettings?.relationshipColumnLabel}</p>
					</Hidden>
					<div>
						{GetMappedRelationshipByValue(beneficiary.relationship, mappedRelationship)}
					</div>
				</div>
				<div className='benefit-table-row-cell'>
					<Hidden smUp>
						<p className='input-label'>{beneficiarySettings?.statusColumnLabel}</p>
					</Hidden>
					<div>
						{RenderStatus()}
					</div>
				</div>
				{hasPercentages &&
					<div className='benefit-table-row-cell'>
						<Hidden smUp>
							<p className='input-label'>{beneficiarySettings?.percentageColumnLabel}</p>
						</Hidden>
						{RenderPercentage()}
					</div>}
				<Hidden smDown>
					<div className='benefit-table-row-cell'>
						{RenderAction()}
					</div>
				</Hidden>
			</div>
		</React.Fragment>
	);
}

BeneficiaryRowView.defaultProps = {
	isUniquePrimary: false,
	onChangeStatus: null,
	onRemove: null,
	statusEditableOnlyForNotDesignated: false,
};